
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchInterpreters } from "slices/interpreterSlice";
import { generateCredentials } from "slices/interpreterSlice";
import { interpreterSelector } from "slices/interpreterSlice";
import Api from "services/api"
import AddInterpreter from "components/interpreter/AddInterpreter";
import toast from "react-hot-toast";
import ConfirmationModal from "components/common/ConfirmationMessage";
import { ListLayout } from "components/common/ListLayout";
import { CustomSwitch } from "components/common/Switch";
import { changeStatus } from "slices/interpreterSlice";
import { removeMultipleInterpreters } from "slices/interpreterSlice";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import { convertTimeToUTC } from "helpers/convertTimeToUTC";
import { usePermissions } from "layouts/Admin";
import { Link } from "react-router-dom";
import { LegendButton } from "components/common/TableColor";
import { useTranslation } from "react-i18next";

const apiService = new Api();

const vendorAgreementLegends = [
  { id: 0, name: "All", value: "all", color: "#3176A0" },
  { id: 1, name: "Expired", value: "expired", color: "#C62828" },
  { id: 2, name: "Expiring Soon", value: "expiring_soon", color: "#C47A00" },
];

const getVendorAgreementLegends = (t = (key) => key) => [
  { id: 0, name: t("vendors.all"), value: "all", color: "#3176A0" },
  { id: 1, name: t("vendors.expired"), value: "expired", color: "#C62828" },
  { id: 2, name: t("vendors.expiringSoon"), value: "expiring_soon", color: "#C47A00" },
];

function InterpreterList() {
  const dispatch = useDispatch()
  const { t } = useTranslation();
  const { checkPermission } = usePermissions();
  const { interpreter: interpreters, last_page: totalPages, loader, total, current_page, per_page: pageLength, agreementAnalytics } = useSelector(interpreterSelector);
  const [addForm, setAddForm] = React.useState({
    show: false,
    item: {}

  });
  const refAddutton = React.useRef(null);
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [selectAll, setSelectAll] = React.useState(false);
  const [deleteModal, setDeleteModal] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState([10])
  const [currentPage, setCurrentPage] = React.useState(1);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [exporting, setExporting] = React.useState(false);
  const [sortConfig, setSortConfig] = React.useState({
    sortBy: 'id',
    sortDirection: 'desc',
  });
  const [activeLegend, setActiveLegend] = React.useState(0);
  const [agreementStatus, setAgreementStatus] = React.useState("all");
  const translatedVendorAgreementLegends = React.useMemo(() => getVendorAgreementLegends(t), [t]);

  React.useEffect(() => {
    dispatch(fetchInterpreters())
  }, [dispatch])

  const handleAddInterpreterPopUp = (item) => {
    setAddForm({
      show: true,
      item,
    });
  };

  const submitCallback = (status, message) => {
    if (status) {
      toast.success(message, {
        className: "successToast",
      })
      setSelectedItems([]);
      setSelectAll(false)
      dispatch(fetchInterpreters(1, { agreement_status: agreementStatus }))
      hideConfirmDialog()
    }
    else {
      toast.error(message,
        {
          className: "errorToast",
        }
      );
    }
  };

  const showConfirmDialog = () => {
    setDeleteModal(true);
    return
  };
  const hideConfirmDialog = () => {
    setDeleteModal(false);
  }
  const handleRemoveItems = () => {
    dispatch(removeMultipleInterpreters({ ids: selectedItems }, submitCallback))
  }

  const handleAgreementLegendClick = (id, value) => {
    setActiveLegend(id);
    setAgreementStatus(value);
    setCurrentPage(1);
    dispatch(fetchInterpreters(1, {
      sortBy: sortConfig.sortBy,
      sortDirection: sortConfig.sortDirection,
      search: searchTerm,
      agreement_status: value,
    }));
  };
  const handleChange = (e) => {
    // handle form change
    const id = e.target.dataset.value
    setSelectedItems(
      selectedItems.includes(id) ?
        selectedItems.filter((item) => item != id) :
        [...selectedItems, id]
    )
  };
  const handleToggleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([])
      setSelectAll(false)
      return
    }
    setSelectAll(true)
    setSelectedItems(interpreters && interpreters?.map((interpreter) => interpreter.id.toString()))
  }

  const handlePageChange = React.useCallback((page) => {
    setCurrentPage(page);
    dispatch(fetchInterpreters(page, { agreement_status: agreementStatus }));
  }, [agreementStatus])

  const handleClose = () => {
    // refAddutton.current.click(); 
    setAddForm({ show: false })
  };

  const handleSelect = (value) => {
    setSelectedValue(value)
  }

  //#region TABLE HEAD 

  const renderTableHeader = React.useCallback(() => {
    const callBack = (dir, sortBy) => {
      setSortConfig({ sortBy, sortDirection: dir })
      dispatch(fetchInterpreters(currentPage, { sortBy: sortBy, sortDirection: dir, search: searchTerm, agreement_status: agreementStatus }))
    }
    return (
      <tr>
        <th className="col-1">
          <div className="row_check">
            <span>#</span>
            {checkPermission('Delete Vendor') &&
              <label className="custom-checkbox checkbox"><input type="checkbox" name="select-all" checked={selectAll} onChange={handleToggleSelectAll} /><span className="checkmark"></span></label>
            }
          </div>
        </th>
        <th className="col-1">
          <TableHeaderItem
            title={t("table.code")}
            sortBy="code"
            sortDirection={sortConfig.sortDirection}
            callBack={callBack}
          />
        </th>
        <th className="col-2">

          <TableHeaderItem
            title={t("table.vendorName")}
            sortBy="name"
            sortDirection={sortConfig.sortDirection}
            callBack={callBack}
          />
        </th>
        <th className="col-2">
          <TableHeaderItem
            title={t("table.email")}
            sortBy="email"
            sortDirection={sortConfig.sortDirection}
            callBack={callBack}
          />
        </th>
        <th className="col-2">
          <TableHeaderItem
            title={t("table.phone")}
            sortBy="phone"
            sortDirection={sortConfig.sortDirection}
            callBack={callBack}
          />
        </th>

        <th className="col-2">
          <TableHeaderItem
            title={t("table.languages")}
            sortBy="languages"
            sortDirection={sortConfig.sortDirection}
            callBack={callBack}
          />
        </th>


        <th className="col-2">
          <TableHeaderItem
            title={t("table.creationDate")}
            sortBy="created_at"
            sortDirection={sortConfig.sortDirection}
            callBack={callBack}
          />
        </th>
        {checkPermission('Status Interpreter') &&
          <th className="col-1">{t("table.status")}</th>
        }
        {checkPermission('Delete Interpreter') &&
          <th className="col-1">{t("table.actions")}</th>
        }
      </tr>
    )
  }, [t, selectAll, searchTerm, sortConfig, currentPage, selectedItems, interpreters, agreementStatus]);

  //#endregion


  //#region TABLE BODY

  const renderTableContent = React.useCallback(() => {
    return (
      Array.isArray(interpreters) && interpreters.map((interpreter, index) => (
        <InterpreterItem interpreter={interpreter} index={index} selectedItems={selectedItems} handleChange={handleChange} handleAddPopUp={handleAddInterpreterPopUp} current_page={current_page} pageLength={pageLength} checkPermission={checkPermission} t={t}/>
      ))
    )
  }, [interpreters, selectedItems, current_page]);

  //#endregion


  const handleGenerateCredentials = () => {
    dispatch(generateCredentials());
  };
  const handleDownloadCsv = async () => {
    setExporting(true);
    try {
      const params = {
        sortBy: sortConfig.sortBy,
        sortDirection: sortConfig.sortDirection,
        search: searchTerm,
        pageLength,
      };
      const response = await Api.exportInterpretersCsv(params);
      if (response?.status >= 200 && response?.status < 300) {
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || `interpreters_${new Date().toISOString().slice(0,10)}.csv`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        const message = response?.data?.message || 'Export failed';
        toast.error(message, { className: 'errorToast' });
      }
    } catch (error) {
      toast.error('Export failed. Please try again.', { className: 'errorToast' });
    }
    setExporting(false);
  }
  const renderCards = () => (
    <div className="d-flex w-100 gap-3 flex-wrap" style={{ marginTop: "14px" }}>
      <div className="border p-3 flex-fill" style={{ minWidth: "240px", borderRadius: "12px", backgroundColor: "#FFF" }}>
        <div className="d-flex mb-2 align-items-center justify-content-between">
          <span style={{ color: "#6C889C" }}>{t("vendors.expiredAgreements")}</span>
          <i className="fa-solid fa-file-circle-xmark" style={{ color: "#C62828", fontSize: "24px" }} />
        </div>
        <span style={{ fontSize: "28px", fontWeight: "bold", color: "#6C889C" }}>
          {loader ? "..." : agreementAnalytics?.expired_agreements_count ?? 0}
        </span>
      </div>
      <div className="border p-3 flex-fill" style={{ minWidth: "240px", borderRadius: "12px", backgroundColor: "#FFF" }}>
        <div className="d-flex mb-2 align-items-center justify-content-between">
          <span style={{ color: "#6C889C" }}>{t("vendors.expiringSoon")}</span>
          <i className="fa-solid fa-file-circle-exclamation" style={{ color: "#C47A00", fontSize: "24px" }} />
        </div>
        <span style={{ fontSize: "28px", fontWeight: "bold", color: "#6C889C" }}>
          {loader ? "..." : agreementAnalytics?.expiring_soon_count ?? 0}
        </span>
      </div>
    </div>
  );

  const renderFilters = () => {
      return (
        <>
          <div className="container-fluid">
            <div className="d-flex justify-content-start flex-wrap mb-4">
              <LegendButton
                activeId={activeLegend}
                options={translatedVendorAgreementLegends}
                handleClick={handleAgreementLegendClick}
              />
            </div>
          </div>
        </>
      );
    };


  return (
    <ListLayout
      heading={t("sidebar.vendors")}
      time={true}
      title=""
      buttonTitle={t("vendors.addVendor")}
      renderFilters={renderFilters}
      renderCards={renderCards}
      renderTableHeader={renderTableHeader}
      renderTableContent={renderTableContent}
      paginationParams={{ agreement_status: agreementStatus }}
      totalPages={totalPages}
      fetchList={fetchInterpreters}
      selectedItems={selectedItems}
      handleRemoveItems={handleRemoveItems}
      renderAddLink={(callback) => checkPermission('Add Vendor') && <Link to="/interpreters/create" className="main_button ml-4">{t("vendors.addVendor")}</Link>}
      addformModal={false}
      refAddutton={refAddutton}
      addForm={addForm}
      showConfirmDialog={showConfirmDialog}
      handleAddPopUp={handleAddInterpreterPopUp}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      sortConfig={sortConfig}
      loader={loader || exporting}
      searchPlaceholder={t("vendors.search")}
      noOfItems={total}
      disableBootstrap={true}
      showMainButton={true}
      onMainButtonClick={handleGenerateCredentials}
      downloadCsv={true}
      onDownloadCsvClick={handleDownloadCsv}
      defaultPageLength={pageLength}
    >

      {deleteModal && <ConfirmationModal onClose={hideConfirmDialog} isOpen={deleteModal} onConfirm={handleRemoveItems} />}
    </ListLayout>
  )

}


const InterpreterItem = ({
  interpreter, index, selectedItems, handleChange, handleAddPopUp, current_page, pageLength, checkPermission, t
}) => {
  const dispatch = useDispatch();
  const [status, setStatus] = React.useState(interpreter?.status == "active" ? 1 : 0 || 0);

  const handleStatusChange = (id, s) => {
    setStatus(s ? 1 : 0)
    dispatch(changeStatus(id, { status: s ? 1 : 0 }));

  }
  useEffect(() => {
    setStatus(
      interpreter?.status
    )

  }, [interpreter]);

  const serial = useMemo(() => {
    return index + 1 + (current_page - 1) * pageLength;
  }, [current_page, pageLength]);

  const getBackgroundStyle = (status) => {
    return status === 0 ? { background: '#f4f4f4' } : {};
  };
  return (
    <tr>
      <td style={getBackgroundStyle(status)}>
        <div className="row_check">
          <span>{serial}</span>
          {checkPermission('Delete Interpreter') &&
            <label className="custom-checkbox checkbox">
              <input type="checkbox" name="select-all" data-value={interpreter.id} checked={selectedItems.includes(interpreter.id.toString())} onChange={handleChange} />
              <span className="checkmark"></span>
            </label>
          }
        </div>
      </td>
      <td style={getBackgroundStyle(status)}>
        {interpreter?.user?.internal_interpreter === 1 && (
          <div style={{ marginBottom: '4px' }}>
            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '999px', backgroundColor: '#3A83B3', color: '#fff', fontSize: '12px', fontWeight: 600 }}>
              {t("vendors.staff")}
            </span>
          </div>
        )}
        {interpreter.code}
      </td>
      <td style={getBackgroundStyle(status)}>
        {interpreter?.user?.name}
      </td>
      <td style={getBackgroundStyle(status)}>
        {interpreter?.user?.email}
      </td>
      <td style={getBackgroundStyle(status)}>
        {interpreter.phone}
      </td>
      <td style={getBackgroundStyle(status)}>
        {
          interpreter?.languages?.map(l => (
            <span key={l?.language_id}>{l?.language?.name}, </span>
          ))
        }
      </td>
      <td style={getBackgroundStyle(status)}>
        {new Date(interpreter.created_at).toISOString().slice(0, 16).replace("T", " ")}
      </td>
      {checkPermission('Status Interpreter') &&
        <td style={getBackgroundStyle(status)}>
          <CustomSwitch handleChange={(status) => handleStatusChange(interpreter?.id, status)} checked={status == 1 ? true : false} />
        </td>
      }
      {checkPermission('Edit Interpreter') &&
        <td style={getBackgroundStyle(status)}>
          <div className="row_check">
            <Link to={`/interpreters/update/${interpreter?.id}`} className="table_edit">{t("vendors.edit")}</Link>
          </div>
        </td>
      }
    </tr>
  )
}


export default InterpreterList;
